import React, { useState, useEffect } from 'react';
import { userAdminAPI, companiesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Plus, Lock, Unlock, KeyRound, Loader2, Copy } from 'lucide-react';

const ROLES = [
  { v: 'super_admin', l: 'Super Admin' },
  { v: 'hr_admin', l: 'HR Admin' },
  { v: 'payroll_admin', l: 'Payroll Admin' },
  { v: 'manager', l: 'Manager' },
  { v: 'employee', l: 'Employee' },
];

const UserAdmin = () => {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(null);
  const [tempPasswordResult, setTempPasswordResult] = useState(null);
  const [form, setForm] = useState({
    email: '', first_name: '', last_name: '', role: 'employee', company_id: '', force_password_change: true,
  });
  const [newPass, setNewPass] = useState('');
  const [acting, setActing] = useState(false);

  const allowedRoles = me?.role === 'super_admin'
    ? ROLES
    : ROLES.filter(r => ['manager', 'employee', 'payroll_admin'].includes(r.v));

  const fetchData = async () => {
    try {
      const [u, c] = await Promise.all([userAdminAPI.list(), companiesAPI.getAll()]);
      setUsers(u.data);
      setCompanies(c.data);
    } catch (e) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async () => {
    if (!form.email || !form.first_name) {
      toast.error('Email and first name required');
      return;
    }
    setActing(true);
    try {
      const res = await userAdminAPI.create(form);
      setTempPasswordResult(res.data);
      setCreateOpen(false);
      setForm({ email: '', first_name: '', last_name: '', role: 'employee', company_id: '', force_password_change: true });
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Create failed');
    } finally {
      setActing(false);
    }
  };

  const handleLockToggle = async (u) => {
    try {
      if (u.is_locked) await userAdminAPI.unlock(u.id); else await userAdminAPI.lock(u.id);
      toast.success(u.is_locked ? 'User unlocked' : 'User locked');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleResetPassword = async () => {
    if (!resetOpen || !newPass || newPass.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setActing(true);
    try {
      await userAdminAPI.resetPassword(resetOpen.id, { new_password: newPass, force_change: true });
      toast.success('Password reset successfully');
      setResetOpen(null);
      setNewPass('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed');
    } finally {
      setActing(false);
    }
  };

  const handleRoleChange = async (u, newRole) => {
    try {
      await userAdminAPI.update(u.id, { role: newRole });
      toast.success('Role updated');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied');
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="user-admin-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">User Administration</h1>
          <p className="text-muted-foreground">Create and manage portal users & roles</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild><Button data-testid="create-user-btn"><Plus size={16} className="mr-2" />Create User</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>A temporary password will be generated</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>First Name</Label><Input value={form.first_name} onChange={(e) => setForm({...form, first_name: e.target.value})} data-testid="new-user-fname" /></div>
                <div><Label>Last Name</Label><Input value={form.last_name} onChange={(e) => setForm({...form, last_name: e.target.value})} data-testid="new-user-lname" /></div>
              </div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} data-testid="new-user-email" /></div>
              <div><Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{allowedRoles.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Company</Label>
                <Select value={form.company_id} onValueChange={(v) => setForm({...form, company_id: v})}>
                  <SelectTrigger><SelectValue placeholder="Select company" /></SelectTrigger>
                  <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={acting} data-testid="save-user-btn">
                {acting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Last Login</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                  <TableCell className="font-medium">{u.first_name} {u.last_name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    {me?.role === 'super_admin' && u.email !== me.email ? (
                      <Select value={u.role} onValueChange={(v) => handleRoleChange(u, v)}>
                        <SelectTrigger className="w-36 h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{ROLES.map(r => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : <Badge variant="outline">{u.role?.replace(/_/g, ' ')}</Badge>}
                  </TableCell>
                  <TableCell>
                    {u.is_locked ? <Badge variant="destructive">Locked</Badge> :
                     u.is_active === false ? <Badge variant="secondary">Inactive</Badge> :
                     <Badge>Active</Badge>}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleString() : '—'}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => handleLockToggle(u)} title={u.is_locked ? 'Unlock' : 'Lock'} data-testid={`lock-toggle-${u.id}`}>
                        {u.is_locked ? <Unlock size={14} /> : <Lock size={14} />}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setResetOpen(u)} title="Reset password" data-testid={`reset-pass-${u.id}`}>
                        <KeyRound size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reset password dialog */}
      <Dialog open={!!resetOpen} onOpenChange={(o) => !o && setResetOpen(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>Reset password for {resetOpen?.email}</DialogDescription>
          </DialogHeader>
          <Input type="password" placeholder="New password (min 6 chars)" value={newPass} onChange={(e) => setNewPass(e.target.value)} data-testid="new-password-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(null)}>Cancel</Button>
            <Button onClick={handleResetPassword} disabled={acting} data-testid="confirm-reset-btn">
              {acting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Show temp password after creation */}
      <Dialog open={!!tempPasswordResult} onOpenChange={() => setTempPasswordResult(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>User Created Successfully</DialogTitle>
            <DialogDescription>Share these credentials with the user. The temporary password will not be shown again.</DialogDescription>
          </DialogHeader>
          {tempPasswordResult && (
            <div className="space-y-3 bg-muted/30 p-4 rounded">
              <div><span className="text-sm text-muted-foreground">Email:</span> <span className="font-mono ml-2">{tempPasswordResult.email}</span></div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Password:</span>
                <code className="font-mono bg-background px-2 py-1 rounded text-sm flex-1" data-testid="temp-password">{tempPasswordResult.temp_password}</code>
                <Button size="sm" variant="outline" onClick={() => copyToClipboard(tempPasswordResult.temp_password)}><Copy size={14} /></Button>
              </div>
            </div>
          )}
          <DialogFooter><Button onClick={() => setTempPasswordResult(null)}>Done</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserAdmin;
